import React from 'react';

// Peer comparison snapshot — styled from the FMP screenshot.
// Static data, but sortable on click for that "live demo" feel.

const PEER_COLS = [
  { k: 'rating',       label: 'Rating',         sub: '%',            align:'right' },
  { k: 'ebMrg',        label: 'EBITDA Margin',  sub: '2025',         align:'right' },
  { k: 'ebFwdMrg',     label: 'EBITDA Margin',  sub: 'fwd · 2y',     align:'right' },
  { k: 'evEbitda',     label: 'EV/EBITDA',      sub: '\u00a0',       align:'right' },
  { k: 'evs',          label: 'EV/S',           sub: '\u00a0',       align:'right' },
  { k: 'fcfeUp',       label: 'FCFE',           sub: 'Upside',       align:'right' },
  { k: 'fcffUp',       label: 'FCFF',           sub: 'Upside',       align:'right' },
  { k: 'pe',           label: 'P/E',            sub: '\u00a0',       align:'right' },
  { k: 'salesCagr',    label: 'Sales CAGR',     sub: 'past 2y, %',   align:'right' },
  { k: 'salesCagrFwd', label: 'Sales CAGR',     sub: 'fwd 2y, %',    align:'right' },
  { k: 'quickRatio',   label: 'Quick Ratio',    sub: '\u00a0',       align:'right' },
];

const PEER_ROWS = [
  { tk:'AAPL', co:"Apple Inc.",                       rating:87.90,  ebMrg:0.35, ebFwdMrg:0.39, evEbitda:25.27,  evs:10.09, fcfeUp: 0.12, fcffUp: 0.11, pe:42.18,  salesCagr:4.98,   salesCagrFwd:6.40,  quickRatio:0.78 },
  { tk:'AMD',  co:"Advanced Micro Devices, Inc.",     rating:90.50,  ebMrg:0.21, ebFwdMrg:0.22, evEbitda:64.11,  evs:13.91, fcfeUp:-0.01, fcffUp:-0.01, pe:112.12, salesCagr:45.75,  salesCagrFwd:22.30, quickRatio:1.94 },
  { tk:'AVGO', co:"Broadcom Inc.",                    rating:115.30, ebMrg:0.54, ebFwdMrg:0.54, evEbitda:36.21,  evs:19.50, fcfeUp:-0.06, fcffUp:-0.06, pe:58.03,  salesCagr:50.37,  salesCagrFwd:24.80, quickRatio:1.05 },
  { tk:'GOOG', co:"Alphabet Inc.",                    rating:99.60,  ebMrg:0.45, ebFwdMrg:0.46, evEbitda:25.67,  evs:10.52, fcfeUp: 0.05, fcffUp:-0.08, pe:44.38,  salesCagr:12.57,  salesCagrFwd:11.20, quickRatio:1.84 },
  { tk:'INTC', co:"Intel Corporation",                rating:81.20,  ebMrg:0.27, ebFwdMrg:0.25, evEbitda:38.98,  evs:9.89,  fcfeUp:-0.55, fcffUp:-0.55, pe:193.76, salesCagr:4.91,   salesCagrFwd:3.10,  quickRatio:1.32 },
  { tk:'MSFT', co:"Microsoft Corporation",            rating:103.50, ebMrg:0.57, ebFwdMrg:0.55, evEbitda:17.77,  evs:9.55,  fcfeUp: 0.06, fcffUp: 0.02, pe:28.01,  salesCagr:15.50,  salesCagrFwd:14.20, quickRatio:1.27 },
  { tk:'NVDA', co:"NVIDIA Corporation",               rating:124.50, ebMrg:0.66, ebFwdMrg:0.51, evEbitda:37.86,  evs:25.35, fcfeUp: 0.13, fcffUp: 0.13, pe:45.60,  salesCagr:127.22, salesCagrFwd:42.50, quickRatio:3.60 },
  { tk:'ORCL', co:"Oracle Corporation",               rating:99.20,  ebMrg:0.42, ebFwdMrg:0.39, evEbitda:23.77,  evs:9.36,  fcfeUp:-0.52, fcffUp:-1.32, pe:76.82,  salesCagr:13.50,  salesCagrFwd:18.40, quickRatio:0.72 },
  { tk:'PLTR', co:"Palantir Technologies Inc.",       rating:112.00, ebMrg:0.32, ebFwdMrg:0.24, evEbitda:166.50, evs:39.73, fcfeUp:-0.21, fcffUp:-0.21, pe:216.08, salesCagr:84.06,  salesCagrFwd:38.60, quickRatio:5.42 },
  { tk:'QCOM', co:"QUALCOMM Incorporated",            rating:86.30,  ebMrg:0.34, ebFwdMrg:0.33, evEbitda:14.61,  evs:4.88,  fcfeUp: 0.53, fcffUp: 0.51, pe:21.59,  salesCagr:4.75,   salesCagrFwd:7.80,  quickRatio:1.85 },
];

export default function PeerSnapshot() {
  const [sortKey, setSortKey] = React.useState('rating');
  const [sortDir, setSortDir] = React.useState('desc');

  const rows = React.useMemo(() => {
    const norm = (r) => {
      const v = r[sortKey];
      if (typeof v === 'string') return parseFloat(v.replace(/[\s']/g, ''));
      return v;
    };
    return [...PEER_ROWS].sort((a, b) => (sortDir === 'desc' ? norm(b) - norm(a) : norm(a) - norm(b)));
  }, [sortKey, sortDir]);

  const avg = (k) => {
    const vals = PEER_ROWS.map(r => typeof r[k] === 'string' ? parseFloat(r[k].replace(/[\s']/g, '')) : r[k]).filter(n => !isNaN(n));
    return vals.reduce((s, v) => s + v, 0) / vals.length;
  };

  const onSort = (k) => {
    if (k === sortKey) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(k); setSortDir('desc'); }
  };

  return (
    <div className="peer-frame">
      <div className="peer-bar">
        <span className="lbl">Sector</span>
        <div className="selector">
          <span>▦</span>
          <b>US High-Tech</b>
          <span className="caret">▾</span>
        </div>
        <span className="lbl" style={{marginLeft:'auto'}}>Data · FMP</span>
        <span className="lbl">Refresh · 4m ago</span>
      </div>
      <div className="peer-meta">
        <h3>US High-Tech</h3>
        <span className="chip"><span className="k">ID</span> 14</span>
        <span className="chip">FMP</span>
        <span className="chip acc">10 companies</span>
        <span className="chip ok">14 indicators</span>
      </div>

      <div className="peer-table-wrap">
        <table className="peer-table">
          <thead>
            <tr>
              <th>Ticker / Company</th>
              {PEER_COLS.map(c => (
                <th key={c.k} className="sortable" onClick={() => onSort(c.k)}>
                  {c.label}
                  {sortKey === c.k && <span className="arr">{sortDir === 'desc' ? '↓' : '↑'}</span>}
                  <span className="sub">{c.sub}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="avg">
              <td>Average (rating)</td>
              <td>{avg('rating').toFixed(2)}</td>
              <td>{avg('ebMrg').toFixed(2)}</td>
              <td>{avg('ebFwdMrg').toFixed(2)}</td>
              <td>{avg('evEbitda').toFixed(2)}</td>
              <td>{avg('evs').toFixed(2)}</td>
              <td>—</td>
              <td>—</td>
              <td>{avg('pe').toFixed(2)}</td>
              <td>{avg('salesCagr').toFixed(2)}</td>
              <td>{avg('salesCagrFwd').toFixed(2)}</td>
              <td>{avg('quickRatio').toFixed(2)}</td>
            </tr>
            {rows.map(r => {
              const rateClass = r.rating >= 110 ? 'hi' : r.rating < 80 ? 'lo' : '';
              const ratePct = Math.max(0, Math.min(100, (r.rating - 60) / 0.8));
              const fcfDelta = Math.abs(r.fcfeUp - r.fcffUp);
              const fcfWarn = fcfDelta >= 0.15;
              return (
                <tr key={r.tk} className={fcfWarn ? 'fcf-warn' : ''}>
                  <td>
                    <span className="ticker">{r.tk}</span>
                    <span className="co">{r.co}</span>
                  </td>
                  <td className={`rating ${rateClass}`}>
                    <span className="bar"><i style={{width:`${ratePct}%`}} /></span>
                    {r.rating.toFixed(2)}
                  </td>
                  <td>{r.ebMrg.toFixed(2)}</td>
                  <td>{r.ebFwdMrg.toFixed(2)}</td>
                  <td>{r.evEbitda.toFixed(2)}</td>
                  <td>{r.evs.toFixed(2)}</td>
                  <td className={r.fcfeUp >= 0 ? 'up' : 'dn'}>{r.fcfeUp >= 0 ? '+' : ''}{r.fcfeUp.toFixed(2)}</td>
                  <td className={r.fcffUp >= 0 ? 'up' : 'dn'}>
                    {r.fcffUp >= 0 ? '+' : ''}{r.fcffUp.toFixed(2)}
                    {fcfWarn && <span className="fcf-flag" title={`FCFE vs FCFF differ by ${fcfDelta.toFixed(2)} — inspect`}>⚠</span>}
                  </td>
                  <td>{r.pe.toFixed(2)}</td>
                  <td className={r.salesCagr >= 0 ? 'up' : 'dn'}>{r.salesCagr >= 0 ? '+' : ''}{r.salesCagr.toFixed(2)}</td>
                  <td className={r.salesCagrFwd >= 0 ? 'up' : 'dn'}>{r.salesCagrFwd >= 0 ? '+' : ''}{r.salesCagrFwd.toFixed(2)}</td>
                  <td>{r.quickRatio.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
